import { createContext, useContext, type ReactElement } from 'react';
import {
  Building2,
  ChartLine,
  FileText,
  FolderInput,
  Handshake,
  Landmark,
  LayoutDashboard,
  Megaphone,
  MonitorSmartphone,
  Settings2,
  ShieldCheck,
  ShoppingCart,
  Users,
} from 'lucide-react';

/**
 * The modules of the navigation. Every module wears the same TAQON brand colours (`tokens`); its
 * name and icon on the page banner are what tell one from another.
 */
export type ModuleKey =
  | 'home'
  | 'reporting'
  | 'organization'
  | 'hr'
  | 'crm'
  | 'marketing'
  | 'quality'
  | 'finance'
  | 'procurement'
  | 'documents'
  | 'assets'
  | 'migration'
  | 'admin';

export interface ModuleTone {
  readonly key: ModuleKey;
  /** The name on the banner badge. */
  readonly name: string;
  readonly icon: ReactElement;
}

export const MODULES: Readonly<Record<ModuleKey, ModuleTone>> = {
  home: {
    key: 'home',
    name: 'Workspace',
    icon: <LayoutDashboard />,
  },
  reporting: {
    key: 'reporting',
    name: 'Reporting',
    icon: <ChartLine />,
  },
  organization: {
    key: 'organization',
    name: 'Organization',
    icon: <Building2 />,
  },
  hr: {
    key: 'hr',
    name: 'Human resources',
    icon: <Users />,
  },
  crm: {
    key: 'crm',
    name: 'Client relationships',
    icon: <Handshake />,
  },
  marketing: {
    key: 'marketing',
    name: 'Marketing',
    icon: <Megaphone />,
  },
  quality: {
    key: 'quality',
    name: 'Quality',
    icon: <ShieldCheck />,
  },
  finance: {
    key: 'finance',
    name: 'Finance',
    icon: <Landmark />,
  },
  procurement: {
    key: 'procurement',
    name: 'Procurement',
    icon: <ShoppingCart />,
  },
  documents: {
    key: 'documents',
    name: 'Documents',
    icon: <FileText />,
  },
  assets: {
    key: 'assets',
    name: 'Assets',
    icon: <MonitorSmartphone />,
  },
  migration: {
    key: 'migration',
    name: 'Data migration',
    icon: <FolderInput />,
  },
  admin: {
    key: 'admin',
    name: 'Administration',
    icon: <Settings2 />,
  },
};

const ModuleContext = createContext<ModuleTone>(MODULES.home);

/** The application frame names the module of the current page; pages read it from here. */
export const ModuleProvider = ModuleContext.Provider;

export function useModule(): ModuleTone {
  return useContext(ModuleContext);
}
